import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonNote,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonToggle,
  IonToolbar,
  isPlatform,
} from "@ionic/react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PageHeader from "./PageHeader";
import { logCustomMessage } from "../util/logger";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { findEmployee } from "../api/Employees";
import { getUserId } from "../util/UserHelper";
import { ColumnsWithGroupData, getColumnsWithGroup } from "../api/Windows";
import { GetColumnsWithGroupField } from "../models/windows/GetColumnsWithGroup";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import ISSElement, { SearchData } from "./ISSElement";
import DropdownElement from "./DropdownElement";
import { barcodeOutline, barcodeSharp, calendarOutline } from "ionicons/icons";
import { useBarcodeManager } from "../hooks/useBarcodeManager";
import { findPO } from "../api/PurchaseOrders";

const EMPLOYEE_ID = "EmployeeId";
const CF_PURCHASE_ORDER = "CfPurchaseOrder";
const BILL_OF_LADING = "BillOfLading";
const COMMENTS = "Comments";

const POReceipts: React.FC = () => {
  const [showLoading, setShowLoading] = useState(false);
  const { translations } = useContext(TranslationsContext);

  const [metadata, setMetadata] = useState<GetColumnsWithGroupField[]>([]);
  const [metadataError, setMetadataError] = useState(false);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [values, setValues] = useState<{ [key: string]: any }>({});
  const [showError, setShowError] = useState<any>({});
  const [customValues, setCustomValues] = useState({});

  const handlePOBarCodeScan = useCallback((value: string) => {
    setValues((pre) => ({ ...pre, CfPurchaseOrder: value }));
  }, []);

  const { scanClick } = useBarcodeManager(handlePOBarCodeScan);

  const updateColumns = useCallback(async () => {
    const data: ColumnsWithGroupData = {
      WindowName: "mob_po_receipts",
      IncludeValidValues: "true",
    };
    const response = await getColumnsWithGroup(data);
    if (response.status !== 200) {
      setMetadataError(true);
    } else if (response.data) {
      // Set metadata and values
      let fields: GetColumnsWithGroupField[] = [];
      let values: { [key: string]: string } = {};
      let keys = Object.keys(response.data.fields);
      keys.forEach((key) => {
        const fieldValue = response.data.fields[key];
        fields.push({
          ...fieldValue,
          filteredValues: fieldValue.ValidValues,
        });
        let propName = fieldValue.PropertyName;
        values[propName] = "";
      });
      fields.sort((a, b) => {
        return Number(a.FieldOrder) - Number(b.FieldOrder);
      });
      setMetadata(fields);
      setValues(values);
    }
  }, []);

  const updateDefaultValues = useCallback(async () => {
    //set logged in employee as default
    const userId = await getUserId();
    setValues((pre) => ({
      ...pre,
      EmployeeId: userId, //currently logged-in user
      DateCharged: new Date().toISOString().slice(0, 10), //todays date
    }));
  }, []);

  useEffect(() => {
    //initial setup
    (async () => {
      setShowLoading(true);
      await updateColumns();
      await updateDefaultValues();
      setShowLoading(false);
    })();
  }, [updateColumns, updateDefaultValues]);

  const handleEmployeeSearch = useCallback(async (keyword: string) => {
    if (keyword.length <= 2) return [];

    const response = await findEmployee(keyword);
    const data = {
      Search: keyword,
    };
    logCustomMessage(
      "Finding employees with typeAhead",
      SeverityLevel.Information,
      "Employees/Find",
      data,
      response
    );
    if (response.status === 200) {
      return Object.entries(response.data).map(
        ([id, name]) =>
          ({
            code: id,
            description: name,
          } as SearchData)
      );
    }
    return [];
  }, []);

  const handlePOSearch = useCallback(async (keyword: string) => {
    if (keyword.length <= 2) return [];

    const response = await findPO(keyword);

    if (response.status === 200) {
      return Object.entries(response.data).map(
        ([id, name]) =>
          ({
            code: id,
            description: name,
          } as SearchData)
      );
    }
    return [];
  }, []);

  const updatePropertyValue = (propertyName: string, newVal: any) => {
    setValues((pre) => ({ ...pre, [propertyName]: newVal }));
  };

  const updateCustomValue = (field: string, value: string) => {
    let temp = JSON.parse(JSON.stringify(customValues));
    temp[field] = value;
    setCustomValues(temp);
  };

  const isInvalidForm = useMemo(() => {
    const hasRequiredFields = metadata.some((m) => m.IsRequired);
    if (!hasRequiredFields) return false;

    const requiredEntries: string[] = metadata.reduce(
      (data: string[], curr) => {
        if (curr.IsRequired && curr.PropertyName) data.push(curr.PropertyName);
        return data;
      },
      []
    );

    return requiredEntries.some((e) => !values[e]);
  }, [metadata, values]);

  const getConfiguredInputField = (field: GetColumnsWithGroupField) => {
    const {
      ValidValues,
      PropertyName = "",
      IsRequired: required,
      IdText = "",
      NetType,
      ElementType,
    } = field;
    const isEmployee = PropertyName === EMPLOYEE_ID;

    if ((isEmployee || PropertyName === CF_PURCHASE_ORDER) && ElementType) {
      const currentValue = values[PropertyName];
      if (ElementType === "Dropdown") {
        return (
          <DropdownElement
            options={ValidValues ?? []}
            value={currentValue}
            title={isEmployee ? "Select Employee" : "Select PO"}
            filterPlaceholder={isEmployee ? "Filter Employee" : "Filter PO"}
            onSelect={(val) => updatePropertyValue(PropertyName, val)}
          />
        );
      } else if (ElementType === "ISS") {
        return (
          <ISSElement
            columnName={isEmployee ? "employee_id" : "cf_purchase_order"}
            tableName={isEmployee ? "em_employee" : "po_v_computed_columns"}
            onSearch={isEmployee ? handleEmployeeSearch : handlePOSearch}
            onSelect={(value) => updatePropertyValue(PropertyName, value)}
            value={currentValue}
            onChange={(val) => updatePropertyValue(PropertyName, val)}
          />
        );
      }
    } else if (ValidValues && ValidValues.length > 0) {
      let currVal = values[PropertyName];
      let selectVal = ValidValues.find((validVal: any) => {
        return validVal.Code === currVal;
      });
      return (
        <IonSelect
          class="wo-editable-input"
          selectedText={!!selectVal ? selectVal.Description : ""}
          interface="popover"
          onIonChange={(ev: any) => {
            if (required) {
              let temp = { ...showError };
              if (
                !ev.detail.value ||
                ev.detail.value === "" ||
                ev.detail.value.length === 0
              ) {
                temp[PropertyName] = true;
                setShowError(temp);
              } else if (
                ev.detail.value.length > 0 &&
                showError[PropertyName]
              ) {
                temp[PropertyName] = false;
                setShowError(temp);
              }
            }
            PropertyName === "StringValue" ||
            PropertyName === "NumericValue" ||
            PropertyName === "DateValue"
              ? updateCustomValue(IdText, ev.detail.value)
              : updatePropertyValue(PropertyName, ev.detail.value);
          }}
          onIonCancel={(ev: any) => {
            let temp = { ...showError };
            if (
              !values[PropertyName] ||
              values[PropertyName] === "" ||
              values[PropertyName].length === 0
            ) {
              temp[PropertyName] = true;
              setShowError(temp);
            } else if (
              values[PropertyName].length > 0 &&
              showError[PropertyName]
            ) {
              temp[PropertyName] = false;
              setShowError(temp);
            }
          }}
        >
          {!required && (
            <IonSelectOption
              key={`${PropertyName}-empty-select-option`}
              value={null}
            >
              {" "}
            </IonSelectOption>
          )}
          {ValidValues.map((value: any, index: number) => {
            return (
              <IonSelectOption
                value={value.Code}
                key={`${PropertyName}-select-option-${index}`}
              >
                {value.Description}
              </IonSelectOption>
            );
          })}
        </IonSelect>
      );
    } else if (NetType === "Boolean") {
      return (
        <IonToggle
          slot="end"
          checked={values[PropertyName]}
          onIonChange={(ev) => {
            updatePropertyValue(PropertyName, ev.detail.checked);
          }}
        ></IonToggle>
      );
    } else if (NetType === "DateTime") {
      const isMobile = isPlatform("ios") || isPlatform("android");

      return (
        <IonItem class="wo-editable-container">
          {/* Native input with date picker */}
          <IonInput
            class="wo-editable-input-assigned"
            required={required}
            value={values[PropertyName]}
            type={"date"}
            onIonInput={(ev: any) => {
              if (required) {
                let temp = { ...showError };
                if (ev.detail.target.value.length === 0) {
                  temp[PropertyName] = true;
                  setShowError(temp);
                } else if (
                  ev.detail.target.value.length > 0 &&
                  showError[PropertyName]
                ) {
                  temp[PropertyName] = false;
                  setShowError(temp);
                }
              }
              PropertyName === "StringValue" ||
              PropertyName === "NumericValue" ||
              PropertyName === "DateValue"
                ? updateCustomValue(IdText, ev.detail.target.value)
                : updatePropertyValue(PropertyName, ev.detail.target.value);
            }}
          ></IonInput>

          {/* Static calendar icon for visual cue */}
          {isMobile && (
            <IonIcon
              icon={calendarOutline}
              slot="end"
              style={{ pointerEvents: "none" }} // prevents blocking input click
            />
          )}
        </IonItem>
      );
    } else if (ElementType === "htmlEditor" || PropertyName === "Comments") {
      return (
        <IonTextarea
          class="wo-editable-input"
          autoGrow
          required={required}
          value={values[PropertyName]}
          onIonChange={(ev: any) => {
            if (required) {
              let temp = { ...showError };
              if (ev.detail.value.length === 0) {
                temp[PropertyName] = true;
                setShowError(temp);
              } else if (
                ev.detail.value.length > 0 &&
                showError[PropertyName]
              ) {
                temp[PropertyName] = false;
                setShowError(temp);
              }
            }
            updatePropertyValue(PropertyName, ev.detail.value);
          }}
        ></IonTextarea>
      );
    } else {
      return (
        <IonInput
          required={required}
          class="wo-editable-input"
          value={values[PropertyName]}
          type={NetType === "DateTime" ? "date" : undefined}
          onIonInput={(ev: any) => {
            if (required) {
              let temp = { ...showError };
              if (ev.detail.target.value.length === 0) {
                temp[PropertyName] = true;
                setShowError(temp);
              } else if (
                ev.detail.target.value.length > 0 &&
                showError[PropertyName]
              ) {
                temp[PropertyName] = false;
                setShowError(temp);
              }
            }
            PropertyName === "StringValue" ||
            PropertyName === "NumericValue" ||
            PropertyName === "DateValue"
              ? updateCustomValue(IdText, ev.detail.target.value)
              : updatePropertyValue(PropertyName, ev.detail.target.value);
          }}
        ></IonInput>
      );
    }
  };

  return (
    <IonPage>
      <PageHeader title={translations["lbl_mob_po_receipts"] || "PO Receipts"}>
        <IonButton onClick={scanClick} slot="end" fill="clear" size="small">
          <IonIcon
            ios={barcodeOutline}
            md={barcodeSharp}
            slot="icon-only"
          ></IonIcon>
        </IonButton>
      </PageHeader>

      <IonLoading
        cssClass="my-custom-class"
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />

      <IonContent fullscreen>
        {metadataError && (
          <IonLabel color="danger">
            {translatedMessages["ErrRetrievingFields"]?.MessageText ||
              "Error retrieving the fields for the creation form"}
          </IonLabel>
        )}

        <IonList class="ion-padding">
          {metadata.map((field, index: number) => {
            const {
              IsRequired: required,
              PropertyName = "",
              TranslatedIdText,
              NetType,
            } = field;
            return (
              <IonItem
                lines="none"
                class="wo-list-item"
                key={`${PropertyName}-${index}`}
              >
                <IonLabel
                  className="wo-editable-input-label"
                  position={
                    NetType === "Boolean" || PropertyName === "WoType"
                      ? undefined
                      : "stacked"
                  }
                  color={required ? "danger" : undefined}
                >
                  {TranslatedIdText}
                  {required && "*"}
                </IonLabel>
                {getConfiguredInputField(field)}
                {required && (
                  <IonNote
                    className="quick-wo-error-note"
                    hidden={!showError[PropertyName]}
                    slot="error"
                    color="danger"
                    key={`${PropertyName}-error`}
                  >
                    {translatedMessages["errIsRequired"]?.MessageText.replace(
                      "@1",
                      TranslatedIdText
                    )}
                  </IonNote>
                )}
              </IonItem>
            );
          })}
        </IonList>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButtons slot="end">
            <IonButton
              fill="solid"
              disabled={isInvalidForm}
              hidden={showLoading}
              routerLink={`/POReceipts/${values[CF_PURCHASE_ORDER]}?billOfLanding=${values[BILL_OF_LADING]}&comments=${values[COMMENTS]}&employee=${values[EMPLOYEE_ID]}`}
            >
              {translations["lbl_submit"] || "Next"}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default POReceipts;
